'use client';

import { useState } from 'react';
import { ChevronDown, Check, Plus, FolderOpen } from 'lucide-react';
import { useProject, ProjectInfo } from '@/contexts/project-context';
import { useSpace } from '@/contexts/space-context';
import { useRouter } from '@/compat/navigation';
import { getIconComponent } from '@/components/icon-picker';

interface ProjectSelectorProps {
  onCreateClick: () => void;
}

export function ProjectSelector({ onCreateClick }: ProjectSelectorProps) {
  const { projects, selectedProject, setSelectedProject, isLoading } = useProject();
  const { space } = useSpace();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);

  const handleProjectSelect = (project: ProjectInfo) => {
    setSelectedProject(project);
    setIsOpen(false);
    router.push(`/spaces/${space.slug}/projects/${project.code}/overview`);
  };

  const SelectedIcon = selectedProject
    ? getIconComponent(selectedProject.icon || 'folder')
    : FolderOpen;

  if (isLoading) {
    return (
      <div className="h-9 bg-muted/50 animate-pulse rounded-md" />
    );
  }

  return (
    <div className="relative" data-project-selector>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md hover:bg-muted transition-colors text-left"
      >
        {selectedProject ? (
          <>
            <div className="w-5 h-5 rounded bg-primary/10 flex items-center justify-center text-primary">
              <SelectedIcon className="w-3 h-3" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-medium text-sm truncate block">
                {selectedProject.name}
              </span>
            </div>
            <span className="text-xs text-muted-foreground font-mono">
              {selectedProject.code}
            </span>
          </>
        ) : (
          <>
            <FolderOpen className="w-4 h-4 text-muted-foreground" />
            <span className="flex-1 text-sm text-muted-foreground">
              Select project
            </span>
          </>
        )}
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-lg shadow-lg z-50 py-1">
            {projects.length === 0 ? (
              <div className="px-3 py-4 text-center text-muted-foreground text-sm">
                <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                No projects yet
              </div>
            ) : (
              <div className="max-h-48 overflow-y-auto">
                {projects.map((project) => {
                  const ProjectIcon = getIconComponent(project.icon || 'folder');
                  return (
                    <button
                      key={project.id}
                      onClick={() => handleProjectSelect(project)}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted transition-colors text-left"
                    >
                      <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center text-primary">
                        <ProjectIcon className="w-3 h-3" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{project.name}</div>
                      </div>
                      <span className="text-xs text-muted-foreground font-mono">
                        {project.code}
                      </span>
                      {selectedProject?.id === project.id && (
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="border-t mt-1 pt-1">
              <button
                onClick={() => {
                  setIsOpen(false);
                  onCreateClick();
                }}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted transition-colors text-left text-primary"
              >
                <div className="w-6 h-6 rounded border-2 border-dashed border-primary/50 flex items-center justify-center">
                  <Plus className="w-3 h-3" />
                </div>
                <span className="text-sm font-medium">Create Project</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
